import { DolfinDiagram } from '../diagram'
import Tvp from '../domains/tvp'
import DolfinCompensationRulesFlowExecutor from '../flow-executor/compensation-rules'
import { BonusOutput, NodeError, NodeWarning, VariableOutput } from '../types'
import { getCompensationValue } from '../utils'
import { DolfinNode } from './base'
import {
  BonusFormDataFixed,
  BonusFormDataMetric,
  BonusFormDataTarget,
  BonusFormDataTiered,
  isBonusProportional,
  isBonusReferencingMetric,
  isBonusReferencingTarget,
  isBonusTiered,
  TypedNode
} from './types'

/**
 * Bonus node - calculates bonus amounts based on target achievement or metrics.
 *
 * Supports calculation types:
 * - `tiered` - Variable payout based on attainment tiers
 * - `proportional` - Linear payout based on achievement
 * - `fixed` - Fixed amount bonus
 *
 * @see /docs/domain/nodes/bonus.md for canonical documentation
 */
export class BonusNode extends DolfinNode<DolfinCompensationRulesFlowExecutor, 'bonus'> {
  private cache: Map<string, unknown> = new Map()

  private shouldScopeOutcomesToExecutionProfile() {
    return !!this.flowExecutor.compensationRule.outcome_period && !!this.flowExecutor.profileId
  }

  private scopeOutcomesToExecutionProfile(
    outcomes: { profile_id: string; outcome: number }[]
  ): { profile_id: string; outcome: number }[] {
    if (!this.shouldScopeOutcomesToExecutionProfile()) {
      return outcomes
    }

    return outcomes.filter(outcome => outcome.profile_id === this.flowExecutor.profileId)
  }

  private getTargetIdByEvaluationMode(
    target: NonNullable<ReturnType<BonusNode['getTargetReferences']>[number]>,
    evaluationMode: 'ltb' | 'period_candidate'
  ) {
    return evaluationMode === 'period_candidate'
      ? (target.period_candidate_target_id ?? target.id)
      : target.id
  }

  private getTargetReferences(formData = this.formData as BonusFormDataTarget) {
    return (formData.targets ?? [formData.target]).filter(
      (target): target is NonNullable<typeof target> => Boolean(target)
    )
  }

  private getTargetEvaluationPeriod(
    target: NonNullable<ReturnType<BonusNode['getTargetReferences']>[number]>,
    period = this.getComputationPeriod(),
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb'
  ) {
    if (
      evaluationMode === 'ltb' &&
      this.hasLongTermBalancing() &&
      this.shouldApplyLongTermOnlyIfHigher() &&
      target.period_candidate_target_id !== undefined
    ) {
      return this.getComputationPeriod(true)
    }

    return period
  }

  async getContextInfo(
    period = this.getComputationPeriod(),
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb'
  ) {
    const attainment = await this.getAttainment(period, evaluationMode)
    const formData = this.formData

    if (isBonusReferencingTarget(formData)) {
      const outcomes = await this.getTargetOutcomes(period, evaluationMode)
      const targets = this.getTargetReferences(formData)
      const contextAttainmentPeriod =
        targets.length === 1
          ? this.getTargetEvaluationPeriod(targets[0], period, evaluationMode)
          : undefined
      const selectedTarget =
        targets.length === 1
          ? {
              ...targets[0],
              id: this.getTargetIdByEvaluationMode(targets[0], evaluationMode),
              target_amount: attainment.target_amount
            }
          : undefined
      const targetContexts = targets.map(target => {
        const targetId = this.getTargetIdByEvaluationMode(target, evaluationMode)
        const targetPeriod = this.getTargetEvaluationPeriod(target, period, evaluationMode)
        const targetAchievement = this.cache.get(
          `attainment,${formData.reference_type},${targetId},${targetPeriod.start},${targetPeriod.end},${evaluationMode}`
        ) as { achievement: number; target_amount?: number } | undefined

        return {
          attainment: targetAchievement?.achievement ?? 0,
          target: {
            ...target,
            id: targetId,
            target_amount: targetAchievement?.target_amount
          },
          value: this.cache.get(
            `target_outcome_value,${targetId},${period.start},${period.end},${evaluationMode}`
          ),
          period,
          ...(targetPeriod.start !== period.start || targetPeriod.end !== period.end
            ? { attainmentPeriod: targetPeriod }
            : {})
        }
      })

      return [
        {
          name: 'context',
          value: {
            attainment: attainment.achievement,
            outcomes,
            target: selectedTarget,
            ...(targetContexts.length > 1 ? { targets: targetContexts } : {}),
            period,
            ...(contextAttainmentPeriod &&
            (contextAttainmentPeriod.start !== period.start ||
              contextAttainmentPeriod.end !== period.end)
              ? { attainmentPeriod: contextAttainmentPeriod }
              : {})
          }
        }
      ]
    }

    return [
      {
        name: 'context',
        value: {
          attainment: attainment.achievement,
          metric: (this.formData as BonusFormDataMetric).metric,
          period
        }
      }
    ]
  }

  async process(): Promise<BonusOutput> {
    if (isBonusTiered(this.formData)) {
      return this.processTiered()
    }

    if (isBonusProportional(this.formData)) {
      const formData = this.formData
      const period = this.getComputationPeriod()
      const attainment = (await this.getAttainment(period, 'ltb')).achievement
      const targetOutcomes = await this.getTargetOutcomes(period, 'ltb')
      const minimumAchievement = formData.minimum_achievement ?? 0
      const canApplyBalancingPeriodPayout = attainment >= minimumAchievement
      const getCappedPercentage = (value: number) =>
        typeof formData.maximum_achievement === 'number' && value > formData.maximum_achievement
          ? formData.maximum_achievement
          : value

      if (
        canApplyBalancingPeriodPayout ||
        (this.hasLongTermBalancing() && this.shouldApplyLongTermOnlyIfHigher())
      ) {
        const percentage = getCappedPercentage(attainment)
        const results: VariableOutput = [
          { name: 'target_outcomes', value: targetOutcomes },
          {
            name: 'bonus_percentage',
            value: percentage
          },
          ...(await this.getContextInfo(period, 'ltb'))
        ]

        if (this.hasLongTermBalancing()) {
          const previousPayouts = await this.getPreviousPayoutsForLongTermBalancing()
          const previousPayoutsTotal = this.sumPreviousPayoutsForLongTermBalancing(previousPayouts)
          let periodCandidateValue: number | undefined
          let periodComparisonData:
            | {
                bonus_percentage: number
                target_outcomes: { profile_id: string; outcome: number }[]
                context: unknown
                compensation_value: number
                period: { start: string; end: string }
              }
            | undefined

          results.push({
            name: 'total_previous_payouts',
            value: previousPayoutsTotal
          })

          const valueBeforeRegulation = canApplyBalancingPeriodPayout
            ? percentage *
              (targetOutcomes.find(outcome => outcome.profile_id === this.flowExecutor.profileId)
                ?.outcome ?? 0)
            : 0

          results.push({
            name: 'compensation_value_before_regulation',
            value: valueBeforeRegulation
          })

          if (this.shouldApplyLongTermOnlyIfHigher()) {
            const currentPeriod = this.getComputationPeriod(true)
            const currentAttainment = (await this.getAttainment(currentPeriod, 'period_candidate'))
              .achievement
            const currentPercentage = getCappedPercentage(currentAttainment)
            const currentOutcomes = await this.getTargetOutcomes(currentPeriod, 'period_candidate')
            const currentContext = await this.getContextInfo(currentPeriod, 'period_candidate')
            const periodValue =
              currentAttainment >= minimumAchievement
                ? currentPercentage *
                  (currentOutcomes.find(
                    outcome => outcome.profile_id === this.flowExecutor.profileId
                  )?.outcome ?? 0)
                : 0
            periodComparisonData = {
              bonus_percentage: currentPercentage,
              target_outcomes: currentOutcomes,
              context: currentContext.find(variable => variable.name === 'context')?.value,
              compensation_value: periodValue,
              period: currentPeriod
            }
            periodCandidateValue = periodValue
          }

          results.push({
            name: 'compensation_value',
            value: valueBeforeRegulation
          })

          if (periodCandidateValue !== undefined) {
            results.push({
              name: 'compensation_value_period_candidate',
              value: periodCandidateValue
            })
          }

          if (periodComparisonData) {
            results.push({
              name: 'compensation_value_comparison',
              value: {
                long_term_balancing: {
                  bonus_percentage: percentage,
                  target_outcomes: targetOutcomes,
                  context: results.find(variable => variable.name === 'context')?.value,
                  compensation_value_before_regulation: valueBeforeRegulation,
                  compensation_value: valueBeforeRegulation - previousPayoutsTotal,
                  period
                },
                period: periodComparisonData
              }
            })
          }
        } else {
          results.push({
            name: 'compensation_value',
            value:
              percentage *
              (targetOutcomes.find(outcome => outcome.profile_id === this.flowExecutor.profileId)
                ?.outcome ?? 0)
          })
        }

        return results as BonusOutput
      }

      return [
        {
          name: 'compensation_value',
          value: 0
        }
      ]
    }

    return this.processFixed()
  }

  private async processFixed(): Promise<BonusOutput> {
    if (!this.flowExecutor.compensationRule.compensation_rule_versions?.[0]?.is_tvp_based) {
      return [
        {
          name: 'compensation_value',
          value: (this.formData as BonusFormDataFixed).fixed_amount ?? 0
        }
      ]
    }

    const profileId = this.flowExecutor.profileId
    const compensationRuleVersion =
      this.flowExecutor.compensationRule.compensation_rule_versions?.[0]

    if (!profileId || !compensationRuleVersion) {
      return this.returnZeroCompensation(
        'Profile or compensation rule version not found for TVP-based fixed bonus'
      )
    }

    const period = this.getComputationPeriod(true)
    const tvpDomain = new Tvp(this.flowExecutor.rpcClient)
    const tvpAllocation = await tvpDomain.getTvpAllocation(
      profileId,
      compensationRuleVersion,
      this.flowExecutor.organizationId,
      period.end
    )

    if (!tvpAllocation) {
      return this.returnZeroCompensation(
        `No TVP allocation found for profile ${profileId} and comp rule ${this.flowExecutor.compensationRule.id}`
      )
    }

    return [
      {
        name: 'compensation_value',
        value: tvpAllocation.allocated_amount
      },
      {
        name: 'tvp_allocation',
        value: tvpAllocation.allocated_amount
      },
      {
        name: 'tvp_percentage',
        value: tvpAllocation.percentage
      },
      {
        name: 'tvp_total',
        value:
          tvpAllocation.percentage === 0
            ? 0
            : tvpAllocation.allocated_amount / tvpAllocation.percentage
      }
    ] satisfies BonusOutput
  }

  private returnZeroCompensation(message?: string): BonusOutput {
    if (message) console.warn(message)

    return [
      {
        name: 'compensation_value',
        value: 0
      },
      {
        name: 'tvp_allocation',
        value: 0
      }
    ]
  }

  private async getAttainment(
    period = this.getComputationPeriod(),
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb'
  ): Promise<{
    achievement: number
    target_amount?: number
  }> {
    const formData = this.formData

    if (isBonusReferencingTarget(formData) && !isBonusProportional(formData)) {
      let attainment = 0
      let targetAmount: number | undefined = undefined
      const targets = this.getTargetReferences(formData)
      const capTargetAchievement =
        'cap_target_achievement' in formData && !!formData.cap_target_achievement
      const useTargetWeights = 'use_target_weights' in formData && !!formData.use_target_weights

      if (targets.length === 0) {
        return { achievement: 0 }
      }

      for (const target of targets) {
        const targetId = this.getTargetIdByEvaluationMode(target, evaluationMode)
        const evaluationPeriod = this.getTargetEvaluationPeriod(target, period, evaluationMode)
        const cacheKey = `attainment,${formData.reference_type},${targetId},${evaluationPeriod.start},${evaluationPeriod.end},${evaluationMode}`
        let targetAchievement = this.cache.get(cacheKey) as
          | { achievement: number; target_amount?: number }
          | undefined

        if (!targetAchievement) {
          targetAchievement = await this.getPeriodTargetAchievement(
            targetId,
            evaluationPeriod.start
          )
          this.cache.set(cacheKey, targetAchievement)
        }

        const targetAch =
          targetAchievement.achievement > 1 && capTargetAchievement
            ? 1
            : targetAchievement.achievement

        attainment +=
          useTargetWeights && targets.length > 1 ? targetAch * (target.weight ?? 1) : targetAch
        targetAmount = targetAchievement.target_amount
      }

      if (targets.length > 1 && !useTargetWeights) {
        attainment /= targets.length ?? 1
      }

      return {
        achievement: attainment,
        target_amount: targets.length === 1 ? targetAmount : undefined
      }
    }

    const referenceType = isBonusReferencingTarget(formData) ? formData.reference_type : 'fixed'
    const referenceId = isBonusReferencingTarget(formData)
      ? this.getTargetReferences(formData)
          .map(target => this.getTargetIdByEvaluationMode(target, evaluationMode))
          .join('|') || undefined
      : isBonusReferencingMetric(formData)
        ? formData.metric.id
        : undefined

    const cacheKey = `attainment,${referenceType},${referenceId ?? 'unknown'},${period.start},${
      period.end
    },${evaluationMode}`

    const cachedAttainment = this.cache.get(cacheKey) as
      | { achievement: number; target_amount?: number }
      | undefined
    if (cachedAttainment) {
      return cachedAttainment
    }

    if (isBonusProportional(formData)) {
      const targets = this.getTargetReferences(formData)
      const proportionalTarget = formData.target ?? targets[0]
      const proportionalTargetId = proportionalTarget
        ? this.getTargetIdByEvaluationMode(proportionalTarget, evaluationMode)
        : undefined
      if (!proportionalTarget || !proportionalTargetId) {
        return { achievement: 0 }
      }

      let proportionalTargetAchievement: { achievement: number; target_amount?: number } | undefined
      for (const target of targets) {
        const targetId = this.getTargetIdByEvaluationMode(target, evaluationMode)
        const evaluationPeriod = this.getTargetEvaluationPeriod(target, period, evaluationMode)
        const targetAchievement = await this.getPeriodTargetAchievement(
          targetId,
          evaluationPeriod.start
        )
        this.cache.set(
          `attainment,${formData.reference_type},${targetId},${evaluationPeriod.start},${evaluationPeriod.end},${evaluationMode}`,
          targetAchievement
        )

        if (targetId === proportionalTargetId) {
          proportionalTargetAchievement = targetAchievement
        }
      }

      if (!proportionalTargetAchievement) {
        const evaluationPeriod = this.getTargetEvaluationPeriod(
          proportionalTarget,
          period,
          evaluationMode
        )
        proportionalTargetAchievement = await this.getPeriodTargetAchievement(
          proportionalTargetId,
          evaluationPeriod.start
        )
        this.cache.set(
          `attainment,${formData.reference_type},${proportionalTargetId},${evaluationPeriod.start},${evaluationPeriod.end},${evaluationMode}`,
          proportionalTargetAchievement
        )
      }

      this.cache.set(cacheKey, proportionalTargetAchievement)
    } else if (isBonusReferencingMetric(formData)) {
      this.cache.set(cacheKey, {
        achievement: await this.getMetricReferenceValue(formData.metric, period.start, period.end)
      })
    }

    return (
      (this.cache.get(cacheKey) as { achievement: number; target_amount?: number } | undefined) ?? {
        achievement: 0
      }
    )
  }

  private async getTargetOutcomes(
    period: { start: string; end: string },
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb'
  ) {
    const formData = this.formData as BonusFormDataTarget
    const outcomes: { profile_id: string; outcome: number }[] = []
    const targets = this.getTargetReferences(formData)
    const useTargetWeights = 'use_target_weights' in formData && !!formData.use_target_weights

    if (targets.length === 0) {
      return outcomes
    }

    for (const target of targets) {
      const targetId = this.getTargetIdByEvaluationMode(target, evaluationMode)
      const targetEvaluationPeriod = this.getTargetEvaluationPeriod(target, period, evaluationMode)
      const cacheKey = `target_outcomes,${targetId},${period.start},${period.end},${this.flowExecutor.profileId},${this.flowExecutor.compensationRule.plan_id},${evaluationMode}`

      let targetOutcomes = this.cache.get(cacheKey) as
        | { profile_id: string; outcome: number }[]
        | undefined
      if (!targetOutcomes) {
        targetOutcomes = await this.flowExecutor.rpcClient.getPeriodTargetOutcomes(
          targetId,
          period.start,
          period.end,
          this.flowExecutor.profileId!,
          this.flowExecutor.compensationRule.plan_id
        )

        this.cache.set(cacheKey, targetOutcomes)
      }

      const scopedTargetOutcomes = this.scopeOutcomesToExecutionProfile(targetOutcomes)
      const targetAchievement = this.cache.get(
        `attainment,${formData.reference_type},${targetId},${targetEvaluationPeriod.start},${targetEvaluationPeriod.end},${evaluationMode}`
      ) as { achievement: number; target_amount?: number } | undefined
      const value = scopedTargetOutcomes.reduce((total, outcome) => total + outcome.outcome, 0)
      this.cache.set(
        `target_outcome_value,${targetId},${period.start},${period.end},${evaluationMode}`,
        value
      )

      this.flowExecutor.execution.captureCalculationInput({
        kind: 'target',
        nodeId: this.node.id,
        target: { ...target, id: targetId },
        value,
        attainment: targetAchievement?.achievement ?? 0,
        ...(typeof targetAchievement?.target_amount === 'number'
          ? { targetAmount: targetAchievement.target_amount }
          : {}),
        period,
        ...(targetEvaluationPeriod.start !== period.start ||
        targetEvaluationPeriod.end !== period.end
          ? { attainmentPeriod: targetEvaluationPeriod }
          : {}),
        count: scopedTargetOutcomes.length
      })

      for (const outcome of scopedTargetOutcomes) {
        const outcomeValue =
          useTargetWeights && targets.length > 1
            ? outcome.outcome * (target.weight ?? 1)
            : outcome.outcome
        const existingOutcome = outcomes.find(o => o.profile_id === outcome.profile_id)
        if (existingOutcome) {
          existingOutcome.outcome += outcomeValue
        } else {
          outcomes.push({
            profile_id: outcome.profile_id,
            outcome: outcomeValue
          })
        }
      }
    }

    if (targets.length > 1 && !useTargetWeights) {
      for (const outcome of outcomes) {
        outcome.outcome /= targets.length ?? 1
      }
    }

    return outcomes
  }

  private async getCompensationValues({
    attainment,
    value,
    period,
    withLongTermBalancing,
    evaluationMode = 'ltb'
  }: {
    attainment: number
    value: string | number
    period: { start: string; end: string }
    withLongTermBalancing: boolean
    evaluationMode?: 'ltb' | 'period_candidate'
  }) {
    if (isBonusReferencingTarget(this.formData)) {
      const outcomes = await this.getTargetOutcomes(period, evaluationMode)
      const compensationValues: {
        profile_id: string
        value: number
      }[] = []
      const compensationValuesBeforeRegulation: {
        profile_id: string
        value: number
      }[] = []
      const previousPayouts: {
        profile_id: string
        value: number
      }[] = []

      for (const { profile_id, outcome } of outcomes) {
        if (withLongTermBalancing) {
          const previousPayoutRecords =
            await this.getPreviousPayoutsForLongTermBalancing(profile_id)
          const previousPayoutsTotal =
            this.sumPreviousPayoutsForLongTermBalancing(previousPayoutRecords)

          previousPayouts.push({
            profile_id,
            value: previousPayoutsTotal
          })

          compensationValuesBeforeRegulation.push({
            profile_id,
            value: (getCompensationValue(value, attainment) / 100) * outcome
          })

          compensationValues.push({
            profile_id,
            value: (getCompensationValue(value, attainment) / 100) * outcome
          })
        } else {
          compensationValues.push({
            profile_id,
            value: (getCompensationValue(value, attainment) / 100) * outcome
          })
        }
      }

      const results: VariableOutput = [
        {
          name: 'compensation_values',
          value: compensationValues
        }
      ]

      if (withLongTermBalancing) {
        results.push({
          name: 'total_previous_payouts',
          value: previousPayouts
        })

        results.push({
          name: 'compensation_values_before_regulation',
          value: compensationValuesBeforeRegulation
        })

        results.push({
          name: 'regulation_period',
          value: this.flowExecutor.compensationRule.long_term_balancing_period
        })
      }

      return results
    }

    const results: VariableOutput = []

    if (withLongTermBalancing) {
      const previousPayouts = await this.getPreviousPayoutsForLongTermBalancing(
        this.flowExecutor.profileId
      )
      const previousPayoutsTotal = this.sumPreviousPayoutsForLongTermBalancing(previousPayouts)

      results.push({
        name: 'compensation_values',
        value: [
          {
            profile_id: this.flowExecutor.profileId,
            value: getCompensationValue(value, attainment)
          }
        ]
      })

      results.push({
        name: 'total_previous_payouts',
        value: previousPayoutsTotal
      })

      results.push({
        name: 'compensation_values_before_regulation',
        value: [
          {
            profile_id: this.flowExecutor.profileId,
            value: getCompensationValue(value, attainment)
          }
        ]
      })

      results.push({
        name: 'regulation_period',
        value: this.flowExecutor.compensationRule.long_term_balancing_period
      })
    } else {
      results.push({
        name: 'compensation_values',
        value: [
          {
            profile_id: this.flowExecutor.profileId,
            value: getCompensationValue(value, attainment)
          }
        ]
      })
    }

    return results
  }

  private getTiers() {
    return [
      ...((this.formData as BonusFormDataTiered).tiers as {
        attainment: number
        value: string | number
      }[])
    ]
  }

  private async processTiered(): Promise<BonusOutput> {
    const period = this.getComputationPeriod()
    const attainment = (await this.getAttainment(period, 'ltb')).achievement

    const tiers = this.getTiers().sort((a, b) => b.attainment - a.attainment)
    const currentTierIndex = tiers.findIndex(
      tier =>
        (isBonusReferencingTarget(this.formData) ? attainment * 100 : attainment) >= tier.attainment
    )

    if (currentTierIndex === -1) {
      return [
        {
          name: 'tier_index',
          value: -1
        },
        {
          name: 'attainment',
          value: attainment
        },
        {
          name: 'compensation_value',
          value: 0
        }
      ]
    }

    const currentTier = tiers[currentTierIndex]
    if (!currentTier) {
      throw new Error('No tier found')
    }

    const compensationValues = await this.getCompensationValues({
      attainment: isBonusReferencingTarget(this.formData) ? attainment * 100 : attainment,
      value: currentTier.value,
      period,
      withLongTermBalancing: this.hasLongTermBalancing(),
      evaluationMode: 'ltb'
    })

    if (this.hasLongTermBalancing() && this.shouldApplyLongTermOnlyIfHigher()) {
      const currentPeriod = this.getComputationPeriod(true)
      const nonLtbAttainment = (await this.getAttainment(currentPeriod, 'period_candidate'))
        .achievement
      const nonLtbTiers = this.getTiers().sort((a, b) => b.attainment - a.attainment)
      const nonLtbTier = nonLtbTiers.find(
        tier =>
          (isBonusReferencingTarget(this.formData) ? nonLtbAttainment * 100 : nonLtbAttainment) >=
          tier.attainment
      )

      if (nonLtbTier) {
        const nonLtbScalarAttainmentValue = isBonusReferencingTarget(this.formData)
          ? nonLtbAttainment * 100
          : nonLtbAttainment
        const nonLtbCompensationValues = await this.getCompensationValues({
          attainment: nonLtbScalarAttainmentValue,
          value: nonLtbTier.value,
          period: currentPeriod,
          withLongTermBalancing: false,
          evaluationMode: 'period_candidate'
        })

        const ltbValues =
          (compensationValues.find(v => v.name === 'compensation_values')?.value as {
            profile_id: string
            value: number
          }[]) ?? []
        const nonLtbValues =
          (nonLtbCompensationValues.find(v => v.name === 'compensation_values')?.value as {
            profile_id: string
            value: number
          }[]) ?? []

        compensationValues.push({
          name: 'compensation_values_period_candidates',
          value: nonLtbValues
        })

        compensationValues.push({
          name: 'compensation_value_comparison',
          value: {
            long_term_balancing: {
              period,
              tier_attainment: currentTier.attainment,
              compensation_values: ltbValues
            },
            period: {
              period: currentPeriod,
              tier_attainment: nonLtbTier.attainment,
              compensation_values: nonLtbValues
            }
          }
        })

        const nonLtbAscendingTiers = [...nonLtbTiers].sort((a, b) => a.attainment - b.attainment)
        const nonLtbTierIndex = nonLtbAscendingTiers.findIndex(
          tierDefinition => tierDefinition === nonLtbTier
        )
        console.table({
          'Non-LTB Tier Index': nonLtbTierIndex,
          'Non-LTB Tier Attainment': nonLtbTier.attainment,
          'Non-LTB Attainment': nonLtbScalarAttainmentValue
        })
      }
    }

    const scalarAttainmentValue = isBonusReferencingTarget(this.formData)
      ? attainment * 100
      : attainment
    const bonusPercentage = getCompensationValue(currentTier.value, scalarAttainmentValue) / 100
    const ascendingTiers = [...tiers].sort((a, b) => a.attainment - b.attainment)
    const tierIndex = ascendingTiers.findIndex(tier => tier === currentTier)
    const targetOutcomes = isBonusReferencingTarget(this.formData)
      ? await this.getTargetOutcomes(period)
      : []
    const rawContext = (await this.getContextInfo(period))[0]?.value
    const context =
      rawContext && typeof rawContext === 'object'
        ? Object.fromEntries(Object.entries(rawContext).filter(([, value]) => value !== undefined))
        : rawContext

    console.table({
      'Tier Index': tierIndex,
      'Tier Attainment': currentTier.attainment,
      Attainment: scalarAttainmentValue
    })

    return [
      {
        name: 'tier_index',
        value: tierIndex
      },
      {
        name: 'attainment',
        value: attainment
      },
      ...(isBonusReferencingTarget(this.formData)
        ? [
            {
              name: 'target_outcomes',
              value: targetOutcomes
            },
            {
              name: 'bonus_percentage',
              value: bonusPercentage
            }
          ]
        : []),
      ...compensationValues,
      {
        name: 'context',
        value: context
      }
    ] as BonusOutput
  }

  async parse(_diagram: DolfinDiagram) {
    return this.node
  }

  static validate(
    node: TypedNode<'bonus'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }
}
